import React, { useState, useRef } from 'react';
import { useInquiry } from '../context/InquiryContext';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Send, MessageCircle, Mail, X } from 'lucide-react';

const InquiryCartPage = () => {
  const { cart, updateQuantity, updateItemNotes, removeFromInquiry, clearInquiry } = useInquiry();
  const navigate = useNavigate();

  const formRef = useRef(null);
  const [showContactOptions, setShowContactOptions] = useState(false);

  const [form, setForm] = useState({
    name: '', phone: '', email: '', company: '', city: '', timeline: 'Immediate', requirements: ''
  });

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validateAndShowRouting = (e) => {
    e.preventDefault();
    if (formRef.current.reportValidity()) {
      setShowContactOptions(true);
    }
  };

  const getCompiledMessage = () => {
    let itemSummary = '';
    cart.forEach((item, index) => {
      itemSummary += `${index + 1}. ${item.name} [Qty: ${item.quantity}] ${item.customNotes ? `(Notes: ${item.customNotes})` : ''}\n`;
    });

    return `*NEW CATALOG INQUIRY - AARTEMA*\n\n` +
      `*Client Profile:*\n` +
      `• Name: ${form.name}\n` +
      `• Company: ${form.company || 'N/A'}\n` +
      `• Contact: ${form.phone} | ${form.email}\n` +
      `• Location: ${form.city}\n` +
      `• Execution Timeline: ${form.timeline}\n\n` +
      `*Requested Inventory List:*\n${itemSummary}\n` +
      `*Additional Requirements:* ${form.requirements || 'None'}`;
  };

  const handleWhatsAppInquiry = (phone) => {
    const urlSafeMessage = encodeURIComponent(getCompiledMessage());
    window.open(`https://wa.me/91${phone}?text=${urlSafeMessage}`, '_blank');
    clearInquiry();
    setShowContactOptions(false);
    navigate('/products');
  };

  const handleEmailInquiry = () => {
    const subject = encodeURIComponent(`New Catalog Inquiry - AARTEMA - ${form.name}`);
    const body = encodeURIComponent(getCompiledMessage().replace(/\*/g, '')); // Clean markdown asterisks for email
    window.location.href = `mailto:trm.iitd@gmail.com,aravmangla20@gmail.com?subject=${subject}&body=${body}`;
    clearInquiry();
    setShowContactOptions(false);
    navigate('/products');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
        <h2 className="text-3xl font-light text-neutral-400 mb-4">Your Inquiry Catalog is Empty</h2>
        <p className="text-gray-500 mb-8 max-w-sm text-center text-sm">Browse our workspace blueprints and seating options to compile a quotation list.</p>
        <Link to="/products" className="px-6 py-3 bg-black text-white rounded-xl font-bold text-sm shadow-md hover:bg-neutral-800 transition">Explore Collection</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 bg-[#FBFBFB] text-left">
      <div className="max-w-7xl mx-auto px-6">
        <h1 className="text-4xl font-bold text-neutral-900 mb-2 tracking-tight">Inquiry Sheet</h1>
        <p className="text-gray-400 text-sm mb-12">Compile and submit your commercial configuration requirements.</p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          <div className="lg:col-span-7 space-y-4">
            {cart.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col md:flex-row gap-6 relative group">
                <div className="w-24 h-24 bg-gray-50 rounded-xl overflow-hidden shrink-0 self-center md:self-start">
                  <img src={item.images && item.images.length > 0 ? item.images[0] : item.image} alt="" className="w-full h-full object-contain mix-blend-multiply" />
                </div>
                
                <div className="flex-grow space-y-3">
                  <div>
                    <span className="text-[10px] font-bold text-[#C1A57B] tracking-widest uppercase">{item.subCategory}</span>
                    <h3 className="font-bold text-lg text-neutral-900 mt-0.5">{item.name}</h3>
                  </div>

                  <input 
                    type="text"
                    placeholder="Specify custom finishes, dimensions, frame configs..."
                    value={item.customNotes || ''}
                    onChange={(e) => updateItemNotes(item.id, e.target.value)}
                    className="w-full text-xs bg-gray-50 border-none rounded-lg py-2.5 px-3 focus:ring-1 focus:ring-neutral-400 text-gray-600"
                  />

                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center bg-gray-50 border rounded-lg overflow-hidden">
                      <button type="button" onClick={() => updateQuantity(item.id, -1)} className="p-2 text-gray-500 hover:bg-gray-100"><Minus size={12} /></button>
                      <span className="px-4 text-xs font-bold text-neutral-800">{item.quantity}</span>
                      <button type="button" onClick={() => updateQuantity(item.id, 1)} className="p-2 text-gray-500 hover:bg-gray-100"><Plus size={12} /></button>
                    </div>

                    <button type="button" onClick={() => removeFromInquiry(item.id)} className="text-gray-400 hover:text-red-500 transition flex items-center gap-1.5 text-xs font-semibold">
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-5 bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
            <h3 className="text-xl font-bold mb-6 text-neutral-900">Requestor Profile</h3>
            
            <form ref={formRef} onSubmit={validateAndShowRouting} className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Full Name *</label>
                <input required type="text" name="name" value={form.name} onChange={handleInputChange} className="w-full p-3 text-sm bg-gray-50 border-none rounded-xl focus:ring-1 focus:ring-black" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Phone Number *</label>
                  <input required type="tel" name="phone" value={form.phone} onChange={handleInputChange} className="w-full p-3 text-sm bg-gray-50 border-none rounded-xl focus:ring-1 focus:ring-black" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Email Address *</label>
                  <input required type="email" name="email" value={form.email} onChange={handleInputChange} className="w-full p-3 text-sm bg-gray-50 border-none rounded-xl focus:ring-1 focus:ring-black" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Company Name</label>
                  <input type="text" name="company" value={form.company} onChange={handleInputChange} className="w-full p-3 text-sm bg-gray-50 border-none rounded-xl focus:ring-1 focus:ring-black" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">City / State *</label>
                  <input required type="text" name="city" value={form.city} onChange={handleInputChange} className="w-full p-3 text-sm bg-gray-50 border-none rounded-xl focus:ring-1 focus:ring-black" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Project Delivery Timeline</label>
                <select name="timeline" value={form.timeline} onChange={handleInputChange} className="w-full p-3 text-sm bg-gray-50 border-none rounded-xl focus:ring-1 focus:ring-black">
                  <option value="Immediate">Immediate (Within 2 weeks)</option>
                  <option value="1 Month">1 Month</option>
                  <option value="2-3 Months">2-3 Months</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">General Project Notes</label>
                <textarea rows="3" name="requirements" value={form.requirements} onChange={handleInputChange} placeholder="E.g., Require wood grain finishes matching..." className="w-full p-3 text-sm bg-gray-50 border-none rounded-xl focus:ring-1 focus:ring-black resize-none"></textarea>
              </div>

              <button type="submit" className="w-full py-4 bg-black text-white text-sm font-bold rounded-xl shadow-lg hover:bg-neutral-800 transition flex items-center justify-center gap-3">
                <Send size={16} /> Choose Submission Method
              </button>
            </form>
          </div>

        </div>
      </div>

      {/* SUBMISSION ROUTING MODAL */}
      {showContactOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative animate-in fade-in zoom-in duration-200 text-left">
            <button onClick={() => setShowContactOptions(false)} className="absolute top-4 right-4 text-gray-400 hover:text-black">
              <X size={20} />
            </button>
            <h3 className="text-2xl font-bold mb-2 text-[#1a1a1a]">Submit Inquiry</h3>
            <p className="text-gray-500 text-sm mb-6">Choose how you'd like to send your inquiry catalog.</p>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                <h4 className="font-bold text-sm mb-1 flex items-center gap-2 text-[#1a1a1a]"><MessageCircle size={16}/> WhatsApp</h4>
                <p className="text-xs text-gray-500 mb-4">Try sending to both numbers for a faster reply.</p>
                <div className="flex gap-3">
                  <button onClick={() => handleWhatsAppInquiry('9289940000')} className="flex-1 bg-[#25D366] text-white py-3 rounded-xl font-bold text-sm hover:bg-[#20BD5A] transition shadow-sm">
                    +91 92899 40000
                  </button>
                  <button onClick={() => handleWhatsAppInquiry('9350056070')} className="flex-1 bg-[#25D366] text-white py-3 rounded-xl font-bold text-sm hover:bg-[#20BD5A] transition shadow-sm">
                    +91 93500 56070
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                <h4 className="font-bold text-sm mb-3 flex items-center gap-2 text-[#1a1a1a]"><Mail size={16}/> Email</h4>
                <button onClick={handleEmailInquiry} className="w-full bg-black text-white py-3 rounded-xl font-bold text-sm hover:bg-neutral-800 transition shadow-sm">
                  Send via Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiryCartPage;
